import { CellProps, Hooks, Renderer } from 'react-table';
import { ReactElement, ReactNode } from 'react';
import { TTableColumn } from './models';
import { formatCost, Identifiable } from '@custis/common';
import ReactDOMServer from 'react-dom/server';
import { DataQuery } from './DataQuery';

export function arraysEqual<T>(a: T[], b: T[]) {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Немного фиксим тип функции-хука, чтобы можно было задать константу с этим типом
 */
export interface FixedPluginHook {
  <D extends object>(hooks: Hooks<D>): void;

  pluginName?: string | undefined;
}

export type RowRenderer<T> = (row: T) => ReactElement;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function renderRow<D extends object = {}, V = any>(renderer: RowRenderer<D>): Renderer<CellProps<D, V>> {
  return (props) => renderer(props.row.original) ?? '';
}

export function formatCellByColumnType<T extends Identifiable, TFilter, TSorting>(
  cellValue: ReactElement,
  column: TTableColumn<T, TFilter, TSorting>,
): ReactNode {
  if (['number', 'integer'].includes(column.type ?? '')) {
    let result = '';
    const valueString = ReactDOMServer.renderToStaticMarkup(cellValue);
    const valueInteger = parseInt(valueString, 10);
    const valueNumber = parseFloat(valueString);
    if (column.type === 'number') {
      if (valueNumber === 0) {
        return '0';
      }
      if (!isNaN(valueNumber)) {
        result = formatCost(valueNumber);
        return result.includes('.') ? formatCost(parseFloat(valueNumber.toFixed(2))) : result + '.00';
      }
      return cellValue;
    }
    if (column.type === 'integer') {
      if (!isNaN(valueInteger)) {
        result = formatCost(valueInteger);
        return result.includes('.') ? result.split('.')[0] : result;
      }
      return cellValue;
    }
    return result;
  }
  return cellValue;
}

export const createStateLink = <T>(pathname: string, urlKey: string, filters: T) => {
  const query = JSON.stringify({
    ...getUrlGridState(),
    filter: filters,
  });
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);
  Array.from(searchParams.keys()).forEach((searchParam) => searchParams.delete(searchParam));
  searchParams.set(urlKey, query);
  return decodeURIComponent(pathname + '?' + searchParams.toString());
};

export function isNotEmpty<T>(value: T | null | undefined): value is T {
  return value !== undefined && value !== null;
}

export function transformStringFromInputNumberFieldToNumber<TItem extends Identifiable, TFilter, TSorting>(
  row: TItem,
  columns: TTableColumn<TItem, TFilter, TSorting>[],
): TItem {
  const result = { ...row };
  Object.entries(row).forEach(([key, value]) => {
    const column = columns.find((item) => item.id === key);
    if ((value || isNaN(value)) && column) {
      if (column.type && ['number', 'integer'].includes(column.type)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        result[key] = getNonNaNFloatValue(value as unknown as string);
      }
    }
  });

  return result;
}

export function getNonNaNFloatValue(value: string): number | undefined {
  const floatValue = parseFloat(value);
  return isNaN(floatValue) ? undefined : floatValue;
}

export function getNonNaNNumberValue(value: string): number | undefined {
  const floatValue = parseInt(value, 10);
  return isNaN(floatValue) ? undefined : floatValue;
}

export function getUrlGridState<TFilter, TSorting>(
  initialState?: DataQuery<TFilter, TSorting>,
): DataQuery<TFilter, TSorting> {
  return {
    sorting: initialState?.sorting,
    paging: initialState?.paging ?? {
      skip: 0,
      count: true,
      top: 20,
      pageIndex: 0,
    },
    filter: initialState?.filter ?? ({} as TFilter),
    selection: initialState?.selection,
  };
}
